import { IGlobalStore, SelectedToken } from "../store/global/GlobalStore";
import { getTokensToApprove } from "./allowanceUtils";
import { some } from "lodash-es";
import { getQuoteWarnings } from "../config/errorCodes";
import { OrderStatus } from "../types/orderStatus";
import { isQuantityLargerThanBalance } from "./orderUtils";
import { ActionButtonStateKeys, InitialActionButtonState } from "../types/initialActionButtonTypes";
import { getWrappedTokenAddress } from "./chainUtils";
import { NATIVE_TOKEN_ADDRESS } from "config/consts";
import { isAddressEqual } from "./tokenUtils";

export const validateOrderEntry = (store: IGlobalStore): ActionButtonStateKeys => {
  const requiredSignatures = store.quote?.requiredSignatures ?? [];
  const hasSignatures = !!(store.permits.permit1.signature || store.permits.permit2.signature);
  const tokensToApprove = getTokensToApprove(store.quote, store.allowances);
  const approvalTarget = store.quote?.approvalTarget;
  const isApprovalNeeded =
    (tokensToApprove.length > 0 && store.permitType === "permit2" && approvalTarget) ||
    (requiredSignatures.length > 0 && approvalTarget && !hasSignatures);
  if (!store.address) {
    return InitialActionButtonState.WalletNotConnected as ActionButtonStateKeys;
  }
  if (store.makerTokens.length === 0 || store.takerTokens.length === 0) {
    return InitialActionButtonState.SelectTokens as ActionButtonStateKeys;
  }

  if (some(store.takerTokens, (entry: SelectedToken) => entry.amount.value.eq(0))) {
    return InitialActionButtonState.EnterAmounts as ActionButtonStateKeys;
  }

  if (store.wrapQuote?.amount !== undefined && store.orderStatus === OrderStatus.ENTRY) {
    if (store.wrapQuote.type === "Wrapping") {
      return InitialActionButtonState.Wrapping as ActionButtonStateKeys;
    } else if (store.wrapQuote.type === "Unwrapping") {
      return InitialActionButtonState.Unwrapping as ActionButtonStateKeys;
    }
  }

  if (isQuantityLargerThanBalance(store)) {
    return InitialActionButtonState.InsufficientBalance as ActionButtonStateKeys;
  }

  if (store.quoteError !== undefined) {
    if (store.quoteError?.error.errorCode === 104 || store.quoteError?.error.errorCode === 106) {
      return InitialActionButtonState.IncreaseOrderSize as ActionButtonStateKeys;
    }
    return InitialActionButtonState.Error as ActionButtonStateKeys;
  }

  if (getQuoteWarnings(store.quote, store.network) && store.isRequestingQuote) {
    return InitialActionButtonState.WarningRequestingQuote as ActionButtonStateKeys;
  }

  if (store.isRequestingQuote) {
    return InitialActionButtonState.RequestingQuote as ActionButtonStateKeys;
  }

  if (store.wrapQuote?.type === undefined && !store.quote) {
    return InitialActionButtonState.NoPricing as ActionButtonStateKeys;
  }

  if (store.orderStatus === OrderStatus.CONFIRMING) {
    return InitialActionButtonState.ConfirmingOrder as ActionButtonStateKeys;
  }
  if (
    some(store.takerTokens, (entry: SelectedToken) =>
      isAddressEqual(entry.tokenAddress, NATIVE_TOKEN_ADDRESS)
    ) &&
    some(
      store.makerTokens,
      (entry: SelectedToken) =>
        !isAddressEqual(entry.tokenAddress, getWrappedTokenAddress(store.network))
    )
  ) {
    return InitialActionButtonState.WrappingQuote as ActionButtonStateKeys;
  }

  if (isApprovalNeeded) {
    return InitialActionButtonState.InsufficientAllowance as ActionButtonStateKeys;
  } else if (requiredSignatures.length > 0 && !hasSignatures) {
    return InitialActionButtonState.InsufficientPermissions as ActionButtonStateKeys;
  }
  if (store.orderStatus === OrderStatus.IN_PROGRESS) {
    return InitialActionButtonState.InProgress as ActionButtonStateKeys;
  }

  if (getQuoteWarnings(store.quote, store.network)) {
    return InitialActionButtonState.IgnoreAndPlaceTrade as ActionButtonStateKeys;
  }

  return InitialActionButtonState.PlaceTrade as ActionButtonStateKeys;
};
